import { DefaultError, QueryFunction, useQuery } from '@tanstack/react-query';

import { GetDeviceAlertConfigurationResponse, GetDeviceAlertOptionsParams } from '@/api/customSensorAlerts/types';
import { axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { SpotterIdQueryKey } from '@/common/types';
import { useViewToken } from '@/context/ViewTokenContext';

export const getDeviceAlertsConfiguration: QueryFunction<GetDeviceAlertConfigurationResponse> = async ({
    queryKey,
    signal,
}): Promise<GetDeviceAlertConfigurationResponse> => {
    const spotterId = (queryKey as SpotterIdQueryKey)[1].spotterId;
    return axios.get(`/user-rest/devices/${spotterId}/alert-configurations`, { signal });
};

export type UseDeviceAlertConfigurationOptions<TData = GetDeviceAlertConfigurationResponse> = {
    config?: QueryConfig<typeof getDeviceAlertsConfiguration, TData>;
} & GetDeviceAlertOptionsParams;

const EMPTY_STATUS = {
    isSuccess: true,
    isError: false,
    isPending: false,
    isLoading: false,
    isFetching: false,
} as const;

const EMPTY_RAW_RESPONSE: GetDeviceAlertConfigurationResponse = { data: [] };

export const useDeviceAlertConfiguration = <TData = GetDeviceAlertConfigurationResponse>({
    spotterId,
    config: { enabled = true, ...config } = {},
}: UseDeviceAlertConfigurationOptions<TData>) => {
    // if we're using a viewToken, alerts are not going to work right since they're attached to the auth'd user, not the owner, so don't
    // bother passing the view token through, just turn them off entirely.
    const { loading: loadingToken, viewToken } = useViewToken();

    const queryResponse = useQuery<GetDeviceAlertConfigurationResponse, DefaultError, TData>({
        ...config,
        enabled: !!spotterId && enabled && !loadingToken && !viewToken,
        queryKey: ['device-alerts-configuration', { spotterId }, { isPublic: Boolean(viewToken) }],
        queryFn: getDeviceAlertsConfiguration,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    if (!viewToken) {
        return queryResponse;
    }

    // if we have a view token, just report a success with no alerts in case anyone's waiting for alerts to load.
    // The caller's `select` runs over that empty payload so it still gets back the shape it asked for.
    return {
        ...EMPTY_STATUS,
        // Without a `select`, TData is its default of the raw response; a defaulted type parameter cannot
        // state that identity, so the compiler cannot prove it here.
        data: config.select ? config.select(EMPTY_RAW_RESPONSE) : (EMPTY_RAW_RESPONSE as TData),
    };
};
